// app/(auth)/login/login-form.tsx
// Login Form Client Component with Show/Hide Password

"use client"

import { useState, useEffect } from "react"
import { useActionState } from "react"
import { useFormStatus } from "react-dom"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { Eye, EyeOff } from "lucide-react"
import { signIn } from "@/app/actions/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CheckCircle2 } from "lucide-react"

function SubmitButton() {
  const { pending } = useFormStatus()

  return (
    <Button type="submit" className="w-full" disabled={pending}>
      {pending ? "Entrando..." : "Entrar"}
    </Button>
  )
}

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [state, formAction] = useActionState(signIn, undefined)
  const router = useRouter()
  const [urlError, setUrlError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Processar search params de forma não-bloqueante no cliente
  useEffect(() => {
    // Processar search params de forma não-bloqueante
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      const error = params.get("error")
      const success = params.get("success")
      
      if (error === "email_confirmation_failed") {
        setUrlError("Falha ao confirmar o email. Por favor, tente novamente ou solicite um novo link de confirmação.")
      } else if (error === "email_not_confirmed") {
        setUrlError("Por favor, confirme seu email antes de acessar o sistema. Verifique sua caixa de entrada e clique no link de confirmação.")
      }
      
      if (success === "email_confirmed") {
        setSuccessMessage("Email confirmado com sucesso! Agora você pode fazer login.")
        // Limpar URL após mostrar mensagem
        const newUrl = window.location.pathname
        router.replace(newUrl)
      }
    }
  }, [router])

  const displayError = state?.error || urlError

  return (
    <form action={formAction}>
      <CardContent className="space-y-4">
        {successMessage && (
          <Alert className="border-green-500 bg-green-50 [&>svg]:text-green-600">
            <CheckCircle2 />
            <AlertDescription className="text-green-900">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}
        {displayError && (
          <Alert variant="destructive">
            <AlertDescription>{displayError}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="assessor@exemplo.com"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Senha</Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        <div className="space-y-3">
          <SubmitButton />
          <div className="text-right">
            <Link
              href="/forgot-password"
              className="text-xs font-medium text-blue-600 hover:underline"
            >
              Esqueci minha senha
            </Link>
          </div>
        </div>
      </CardContent>
    </form>
  )
}
